// KLUB-283 U1 — pt-BR grammar for a club's vocabulary noun.
//
// One source for every gender- and number-driven form of a noun the club
// configured (member, prospect, tier, ...): articles, possessives,
// demonstratives, contractions, "Novo/Nova" and adjective agreement. The app,
// the admin and the edge functions all build sentences through it, so a
// feminine term ("Categoria") never lands in a sentence written for a
// masculine one ("Meu Categoria").
//
// ⚠️ THIS FILE IMPORTS NOTHING, not even types. Deno loads it by relative path
// (`../../../packages/shared/src/tenant-settings/nounGrammar.ts`), like
// `codeVersion.ts` and `renewalFuture.ts`. An extensionless import breaks
// Deno; a `.ts` one breaks the mobile `tsc` (TS5097). The input is therefore
// structural: anything with `singular`, `plural` and an optional `gender`.
//
// MISSING GENDER IS NOT MASCULINE. A vocabulary row may carry no `gender`
// leaf (The Latvian's prospect and tier have none), and the right reading is
// the gender of the platform default for that noun, which only the caller
// knows. Every entry point therefore takes the fallback explicitly.
//
// CASE. A stored term keeps the club's capitalization in titles ("Meu Plano").
// Mid-sentence it is lowercased as a whole with the pt-BR rule ("não
// associado"), and a sentence start capitalizes only the first letter. A term
// is never re-capitalized word by word.

export type NounGender = "m" | "f";

/** Structural vocabulary noun. `gender` is `string` on purpose: generated
 *  mobile `settings.ts` is `as const`, so a literal type would make a
 *  comparison against the other gender unreachable for that tenant. */
export interface GrammarNoun {
  singular: string;
  plural: string;
  gender?: string | null;
}

/** "m"/"f" as stored; anything else (missing, empty, invalid) is `fallback`. */
export function resolveGender(gender: unknown, fallback: NounGender): NounGender {
  return gender === "f" || gender === "m" ? gender : fallback;
}

/** Whole-term pt-BR lowercase for a term in the middle of a sentence. */
export function lowerTerm(term: string): string {
  return term.toLocaleLowerCase("pt-BR");
}

/** Sentence-start form: the lowercased term with only its first letter raised. */
export function capTerm(term: string): string {
  const lower = lowerTerm(term);
  if (lower.length === 0) return lower;
  return lower.charAt(0).toLocaleUpperCase("pt-BR") + lower.slice(1);
}

/** Raises only the first letter of a phrase that is already built. */
function capFirst(phrase: string): string {
  if (phrase.length === 0) return phrase;
  return phrase.charAt(0).toLocaleUpperCase("pt-BR") + phrase.slice(1);
}

/** Picks the masculine or feminine word for the noun's gender. */
export function agree(
  noun: { gender?: string | null },
  masculine: string,
  feminine: string,
  fallback: NounGender,
): string {
  return resolveGender(noun.gender, fallback) === "f" ? feminine : masculine;
}

/**
 * Plural of a pt-BR adjective, for the endings this codebase uses:
 * vowel → +s ("aberto" → "abertos"), -m → -ns ("comum" → "comuns"),
 * -al/-el/-ol/-ul → -ais/-eis/-ois/-uis ("disponível" → "disponíveis"),
 * -r/-z → +es ("maior" → "maiores").
 * Not covered: "-il" ("útil" → "úteis") and accent shifts ("fiel" → "fiéis").
 * Pass the plural words to `agree` for those.
 */
export function pluralAdjective(adjective: string): string {
  if (/[aeiouáéíóúâêôãõ]$/i.test(adjective)) return `${adjective}s`;
  if (/m$/i.test(adjective)) return `${adjective.slice(0, -1)}ns`;
  if (/[aeou]l$/i.test(adjective)) return `${adjective.slice(0, -1)}is`;
  if (/[rz]$/i.test(adjective)) return `${adjective}es`;
  return `${adjective}s`;
}

/** Adjective agreement in the plural: gender picks the word, then pluralizes it. */
export function agreePlural(
  noun: { gender?: string | null },
  masculine: string,
  feminine: string,
  fallback: NounGender,
): string {
  return pluralAdjective(agree(noun, masculine, feminine, fallback));
}

/** "Novo" or "Nova". Callers pair it with the stored or lowered term. */
export function newPrefix(noun: { gender?: string | null }, fallback: NounGender = "m"): "Novo" | "Nova" {
  return resolveGender(noun.gender, fallback) === "f" ? "Nova" : "Novo";
}

/** Every form of one noun, resolved once. Lowercase keys are mid-sentence. */
export interface NounForms {
  gender: NounGender;
  feminine: boolean;
  /** Stored term, club capitalization, for titles and labels. */
  singular: string;
  plural: string;
  /** "plano" / "não associado". */
  singularLower: string;
  pluralLower: string;
  /** "Plano" / "Não associado": first letter raised, rest lowercase. */
  singularCap: string;
  pluralCap: string;
  /** "o plano" / "a categoria". */
  the: string;
  /** "os planos" / "as categorias". */
  thePlural: string;
  /** "um plano" / "uma categoria". */
  a: string;
  /** "seu plano" / "sua categoria". */
  your: string;
  /** "Seu plano" / "Sua categoria". */
  yourCap: string;
  /** "seus planos" / "suas categorias". */
  yourPlural: string;
  yourPluralCap: string;
  /** "Meu Plano" / "Minha Categoria": title form, stored capitalization. */
  myTitle: string;
  /** "este plano" / "esta categoria". */
  this: string;
  thisCap: string;
  /** "esse plano" / "essa categoria". */
  that: string;
  thatCap: string;
  /** "do plano" / "da categoria". */
  ofThe: string;
  /** "dos planos" / "das categorias". */
  ofThePlural: string;
  /** "no plano" / "na categoria". */
  inThe: string;
  /** "nos planos" / "nas categorias". */
  inThePlural: string;
  /** "deste plano" / "desta categoria". */
  ofThis: string;
  /** "neste plano" / "nesta categoria". */
  inThis: string;
  /** "Novo" / "Nova". */
  newWord: "Novo" | "Nova";
  /** "Novo plano" / "Nova categoria". */
  newSingular: string;
}

/**
 * All gendered forms of `noun`. `fallback` is the gender of the platform
 * default for the same noun (e.g. `VOCABULARY_DEFAULTS.activity.gender`).
 */
export function nounGrammar(noun: GrammarNoun, fallback: NounGender): NounForms {
  const gender = resolveGender(noun.gender, fallback);
  const f = gender === "f";
  const singularLower = lowerTerm(noun.singular);
  const pluralLower = lowerTerm(noun.plural);
  const pick = (masculine: string, feminine: string): string => (f ? feminine : masculine);

  const your = `${pick("seu", "sua")} ${singularLower}`;
  const yourPlural = `${pick("seus", "suas")} ${pluralLower}`;
  const thisForm = `${pick("este", "esta")} ${singularLower}`;
  const thatForm = `${pick("esse", "essa")} ${singularLower}`;
  const newWord = f ? "Nova" : "Novo";

  return {
    gender,
    feminine: f,
    singular: noun.singular,
    plural: noun.plural,
    singularLower,
    pluralLower,
    singularCap: capTerm(noun.singular),
    pluralCap: capTerm(noun.plural),
    the: `${pick("o", "a")} ${singularLower}`,
    thePlural: `${pick("os", "as")} ${pluralLower}`,
    a: `${pick("um", "uma")} ${singularLower}`,
    your,
    yourCap: capFirst(your),
    yourPlural,
    yourPluralCap: capFirst(yourPlural),
    myTitle: `${pick("Meu", "Minha")} ${noun.singular}`,
    this: thisForm,
    thisCap: capFirst(thisForm),
    that: thatForm,
    thatCap: capFirst(thatForm),
    ofThe: `${pick("do", "da")} ${singularLower}`,
    ofThePlural: `${pick("dos", "das")} ${pluralLower}`,
    inThe: `${pick("no", "na")} ${singularLower}`,
    inThePlural: `${pick("nos", "nas")} ${pluralLower}`,
    ofThis: `${pick("deste", "desta")} ${singularLower}`,
    inThis: `${pick("neste", "nesta")} ${singularLower}`,
    newWord,
    newSingular: `${newWord} ${singularLower}`,
  };
}
